import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { PublicLayout } from '@/components/public/PublicLayout';
import { Seo } from '@/components/Seo';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { EventTicketDialog } from '@/components/public/EventTicketDialog';
import { toDialogEvent } from '@/pages/public/Eventi';
import { eventsService } from '@/services/events.service';
import { mediaUrl } from '@/lib/api';
import { setCategories, EVENTS_HERO_IMG, euro, fmtLongDate, categoryMeta, catColor } from '@/lib/eventsMock';
import { CalendarDays, MapPin, Clock, Ticket, ArrowLeft, CalendarX2, ShieldCheck, Users } from 'lucide-react';

const coverOf = (c) => (c ? mediaUrl(c) : EVENTS_HERO_IMG);

export default function EventoDettaglio() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ev, setEv] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setNotFound(false);
      try {
        const [det, catsRes] = await Promise.all([
          eventsService.publicGet(slug),
          eventsService.publicCategories().catch(() => ({ categories: [] })),
        ]);
        if (!alive) return;
        setCategories(catsRes.categories || []);
        setEv(det);
      } catch (err) {
        if (alive) setNotFound(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  const meta = ev ? categoryMeta(ev.category) : null;
  const priceFrom = useMemo(() => {
    const prices = (ev?.ticket_types || []).map((t) => t.price).filter((n) => n >= 0);
    return prices.length ? Math.min(...prices) : 0;
  }, [ev]);
  const firstDate = ev?.sessions?.[0]?.start ? new Date(ev.sessions[0].start) : null;

  if (loading) {
    return (
      <PublicLayout>
        <div className="sila-section py-10">
          <Skeleton className="h-64 w-full rounded-2xl" />
          <div className="mt-6 grid gap-6 lg:grid-cols-3">
            <div className="space-y-3 lg:col-span-2"><Skeleton className="h-8 w-1/2" /><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-2/3" /></div>
            <Skeleton className="h-56 w-full rounded-2xl" />
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (notFound || !ev) {
    return (
      <PublicLayout>
        <div className="sila-section flex flex-col items-center py-20 text-center" data-testid="evento-notfound">
          <CalendarX2 className="h-12 w-12 text-muted-foreground" />
          <h1 className="mt-4 font-display text-2xl font-bold text-foreground">Evento non disponibile</h1>
          <p className="mt-1 text-muted-foreground">Potrebbe essere terminato o non ancora pubblicato.</p>
          <Button className="mt-6 rounded-full" onClick={() => navigate('/eventi')} data-testid="evento-back-to-list"><ArrowLeft className="mr-2 h-4 w-4" /> Tutti gli eventi</Button>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <Seo title={`${ev.title} — Eventi Sila`} description={ev.short || ev.description?.slice(0, 150)} image={coverOf(ev.cover)} />

      {/* HERO */}
      <div className="relative h-[46vh] min-h-[320px] w-full overflow-hidden">
        <img src={coverOf(ev.cover)} alt={ev.title} className="h-full w-full object-cover" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.7) 100%)' }} />
        <div className="sila-section absolute inset-x-0 bottom-0 pb-8">
          <Link to="/eventi" className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-white/90 hover:text-white" data-testid="evento-breadcrumb-back"><ArrowLeft className="h-4 w-4" /> Eventi</Link>
          <span className="mb-3 inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold text-white backdrop-blur" style={{ backgroundColor: catColor(ev.category, 0.85) }}>{meta.label}</span>
          <h1 className="font-display text-3xl font-bold leading-tight text-white drop-shadow sm:text-4xl" data-testid="evento-title">{ev.title}</h1>
          <p className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-white/90">
            {firstDate && <span className="flex items-center gap-1.5"><CalendarDays className="h-4 w-4" />{fmtLongDate(firstDate)}</span>}
            {ev.location && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" />{ev.location}</span>}
            {ev.owner_name && <span className="flex items-center gap-1.5"><Users className="h-4 w-4" />{ev.owner_name}</span>}
          </p>
        </div>
      </div>

      <div className="sila-section grid gap-8 py-10 lg:grid-cols-3">
        {/* Colonna contenuti */}
        <div className="lg:col-span-2">
          <h2 className="font-display text-xl font-semibold text-foreground">Descrizione</h2>
          <p className="mt-3 whitespace-pre-line text-[15px] leading-relaxed text-muted-foreground" data-testid="evento-description">{ev.description || ev.short}</p>

          {ev.sessions?.length > 0 && (
            <div className="mt-8">
              <h2 className="font-display text-xl font-semibold text-foreground">Date disponibili</h2>
              <div className="mt-3 grid gap-2 sm:grid-cols-2" data-testid="evento-sessions">
                {ev.sessions.map((s) => (
                  <div key={s.id} className="flex items-center justify-between rounded-xl border border-border bg-card p-3">
                    <span className="flex items-center gap-2 text-sm font-medium text-foreground"><CalendarDays className="h-4 w-4" style={{ color: catColor(ev.category) }} />{fmtLongDate(new Date(s.start))}<span className="text-muted-foreground">· {s.time}</span></span>
                    <span className="text-xs text-muted-foreground">{s.left > 900 ? 'Disponibile' : s.left <= 15 ? `Ultimi ${s.left}` : `${s.left} posti`}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {ev.ticket_types?.length > 0 && (
            <div className="mt-8">
              <h2 className="font-display text-xl font-semibold text-foreground">Biglietti</h2>
              <div className="mt-3 space-y-2" data-testid="evento-ticket-types">
                {ev.ticket_types.map((t) => (
                  <div key={t.id} className="flex items-center justify-between rounded-xl border border-border bg-card p-3">
                    <span><span className="text-sm font-semibold text-foreground">{t.label}</span>{t.desc && <span className="ml-2 text-xs text-muted-foreground">{t.desc}</span>}</span>
                    <span className="text-sm font-semibold tabular-nums text-foreground">{euro(t.price)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Colonna acquisto (sticky) */}
        <aside className="lg:col-span-1">
          <div className="sticky top-24 rounded-2xl border border-border bg-card p-5 shadow-sm" data-testid="evento-purchase-card">
            <p className="text-sm text-muted-foreground">Biglietti da</p>
            <p className="font-display text-3xl font-bold text-foreground">{euro(priceFrom)}</p>
            <Button className="mt-4 h-11 w-full rounded-full text-sm font-semibold" onClick={() => setBuyOpen(true)} data-testid="evento-buy-button">
              <Ticket className="mr-2 h-4 w-4" /> Acquista biglietto
            </Button>
            <Separator className="my-4" />
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4" style={{ color: catColor('festival') }} /> Prezzo calcolato lato server</li>
              <li className="flex items-center gap-2"><Ticket className="h-4 w-4" style={{ color: catColor('festival') }} /> QR immediato all’acquisto</li>
              <li className="flex items-center gap-2"><Clock className="h-4 w-4" style={{ color: catColor('festival') }} /> Conferma via email</li>
            </ul>
          </div>
        </aside>
      </div>

      <EventTicketDialog event={buyOpen ? toDialogEvent(ev) : null} open={buyOpen} onClose={() => setBuyOpen(false)} />
    </PublicLayout>
  );
}
